// The Stations screen (spec/13 §12; F-STN-21 classes, F-STN-19 zones): three tabs under one page
// header — Stations, Classes and Zones.
//
//   - Stations (`StationsTab`): the venue's PCs and consoles — search, filter by class, add and pair.
//   - Classes  (`ClassesTab`): the price and permission tiers, as cards with a live-preview dialog.
//   - Zones    (`ZonesTab`):  the named areas of the floor, grouped by level.
//
// A member without `stations:manage` sees Stations and Classes read-only; without `floor:arrange`,
// Zones read-only. The server checks each permission again on every request (spec/13 §1).
//
// The tab is in the address (`?tab=classes`, `?tab=zones`; Stations without one), so a link — Setup's, a colleague's —
// lands on the right tab, and the back button undoes a tab change.
//
// A venue with Clavian Boot and no Clavian Stations (ADR-0065, spec/13 §12) has Classes and Zones only — its list of PCs is
// the Floor's — and opens on Classes, where each class decides what its PCs start (F-BOOT-27).

import { useTranslation } from '@clavian/i18n/react';
import { Tabs, type TabItem } from '@clavian/ui-web';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { useAccess } from '../api/queries';
import { useCurrentVenue } from '../shell/scope';
import { Notice, Page } from '../ui';
import { ClassesTab } from './ClassesTab';
import { StationsTab } from './StationsTab';
import { ZonesTab } from './ZonesTab';

import './stations.css';

const STATION_MANAGE = 'stations:manage';
const FLOOR_ARRANGE = 'floor:arrange';
const BOOT_VIEW = 'boot:view';
const BOOT_MANAGE = 'boot:manage';

/** The Stations screen's tabs, as its address names them. */
export const STATIONS_TABS = ['stations', 'classes', 'zones'] as const;
export type StationsTab = (typeof STATIONS_TABS)[number];

/** The route's `validateSearch`: a tab it knows, or none (Stations). */
export function readStationsSearch(search: Record<string, unknown>): { tab?: StationsTab } {
  const tab = search['tab'];
  return typeof tab === 'string' && (STATIONS_TABS as readonly string[]).includes(tab) && tab !== 'stations' ? { tab: tab as StationsTab } : {};
}

export function StationsPage(): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const access = useAccess({ venueId: venue.id });
  const ready = access.status === 'ready';
  const canManage = ready && access.can(STATION_MANAGE);
  const canArrange = ready && access.can(FLOOR_ARRANGE);
  const navigate = useNavigate();
  // Boot only: no Stations tab (the Floor lists the PCs), so the page opens on Classes.
  const bootOnly = ready && !access.products.stations && access.products.boot;
  const bootView = ready && access.products.boot && access.can(BOOT_VIEW);
  const boot = { view: bootView, edit: bootView && canManage && access.can(BOOT_MANAGE) };
  const asked = readStationsSearch(useSearch({ strict: false }) as Record<string, unknown>).tab ?? 'stations';
  const tab = bootOnly && asked === 'stations' ? 'classes' : asked;

  const tabs: readonly TabItem[] = [
    ...(bootOnly
      ? []
      : [
          {
            value: 'stations',
            label: t('stations.tabs.stations'),
            content: <StationsTab organizationId={venue.organizationId} venueId={venue.id} canManage={canManage} />,
          },
        ]),
    {
      value: 'classes',
      label: t('stations.tabs.classes'),
      content: <ClassesTab organizationId={venue.organizationId} venueId={venue.id} canManage={canManage} boot={boot} />,
    },
    {
      value: 'zones',
      label: t('stations.tabs.zones'),
      content: <ZonesTab organizationId={venue.organizationId} venueId={venue.id} canArrange={canArrange} />,
    },
  ];

  return (
    <Page title={t('stations.title')} description={t('stations.intro')}>
      {ready && !canManage ? <Notice role="note">{t('stations.readOnly')}</Notice> : null}
      <Tabs
        label={t('stations.tabs.label')}
        items={tabs}
        value={tab}
        onValueChange={(next) => void navigate({ to: '.', search: next === 'stations' || (bootOnly && next === 'classes') ? {} : { tab: next } } as never)}
        className="console-page-tabs"
      />
    </Page>
  );
}
